import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { LoginErrorType } from 'src/app/core/services/const';
import { LoaderService } from 'src/app/core/services/loader/loader.service';
import { AuthService } from '../auth/auth.service';
import { AuthResponseDto } from '../auth/response/authResponseDto.model';
import { ClientLoggerService } from 'src/app/core/services/logging/client-logger.service';
import { LogCategory } from 'src/app/core/services/logging/client-log.models';
import { SubscriptionService } from 'src/app/modules/feature-toggle/services/subscription.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export function authInterceptor(
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> {
  const router = inject(Router);
  const loaderService = inject(LoaderService);
  const authService = inject(AuthService);
  const logger = inject(ClientLoggerService);

  // Skip interceptor for translation files and static assets
  if (isStaticAssetRequest(req)) {
    return next(req);
  }

  let authReq = req;
  const token = localStorage.getItem('token');

  if (token != null && !isRefreshRequest(req)) {
    authReq = addTokenHeader(req, token);
  }

  loaderService.isLoading.next(loaderService.isLoading.getValue() + 1);

  return next(authReq).pipe(
    finalize(() =>
      loaderService.isLoading.next(loaderService.isLoading.getValue() - 1)
    ),
    catchError((error) => {
      if (error.error?.ErrorType === LoginErrorType.RefreshTokenExpired) {
        // LOGGING: Refresh-Token abgelaufen
        logger.logWarning('Auth Interceptor: Refresh token expired - redirecting to login', LogCategory.Security, {
          componentName: 'AuthInterceptor',
          errorType: 'RefreshTokenExpired',
          url: authReq.url,
          method: authReq.method
        });

        clearAuthAndRedirect(router, logger);
        return throwError(() => error);
      }

      // Ein 403 auf einer Terminal-Route erreichte den Nutzer bis zum 13.09.2026 nie: Der
      // Interceptor kannte nur 401, und der HttpService stuft einen 403 ohne `notifications` als
      // technischen Fehler ein und unterdrückt ihn. Am Wandgerät blieb genau eine leere Liste
      // übrig — ohne Meldung, ohne Weg zurück. Der Fall entstand, weil das erneuerte Token die
      // Rolle in falscher Schreibweise trug (im Backend behoben). Diese Behandlung bleibt als
      // Sicherheitsnetz: Ein Rechtefehler am Terminal muss sichtbar sein.
      if (
        error instanceof HttpErrorResponse &&
        error.status === 403 &&
        isTerminalRoute(authReq) &&
        isTerminalUser()
      ) {
        logger.logError(new Error('Terminal route denied with 403'), {
          componentName: 'AuthInterceptor',
          category: LogCategory.Security,
          properties: {
            url: authReq.url,
            method: authReq.method,
            errorType: 'TerminalForbidden',
            hint: 'Token trägt vermutlich nicht die Rolle TERMINAL — Gerät neu koppeln.',
          },
        });

        localStorage.setItem('terminalSessionExpired', 'true');
        if (!router.url.includes('/terminal-dashboard')) {
          router.navigateByUrl('/terminal-dashboard');
        }

        // Kein eigener Meldungstext: Ausgewertet wird das Flag. Das Terminal-Dashboard zeigt
        // daraus seinen eigenen, uebersetzten Hinweis.
        return throwError(() => ({
          ...error,
          terminalSessionExpired: true,
        }));
      }

      if (
        error instanceof HttpErrorResponse &&
        !isAuthEndpoint(authReq) &&
        !isRefreshRequest(authReq) && // WICHTIG: Verhindere Endlosschleife wenn Refresh-Token abgelaufen
        !isPinValidationRequest(authReq) &&
        error.status === 401
      ) {
        // LOGGING: 401 Unauthorized - versuche Token-Refresh
        logger.logWarning('Auth Interceptor: 401 Unauthorized - attempting token refresh', LogCategory.Security, {
          componentName: 'AuthInterceptor',
          url: authReq.url,
          method: authReq.method,
          statusCode: error.status,
          hasToken: !!localStorage.getItem('token'),
          hasRefreshToken: !!localStorage.getItem('refreshToken'),
          isRefreshing: isRefreshing
        });

        return handle401Error(authReq, next, router, authService, logger);
      }

      // WICHTIG: Wenn RefreshToken-Endpoint 401 zurückgibt, ist Refresh-Token abgelaufen
      if (
        error instanceof HttpErrorResponse &&
        isRefreshRequest(authReq) &&
        error.status === 401
      ) {
        // LOGGING: Refresh-Token abgelaufen
        logger.logError(
          new Error('Refresh Token expired or invalid'),
          {
            componentName: 'AuthInterceptor',
            category: LogCategory.Security,
            properties: {
              url: authReq.url,
              method: authReq.method,
              statusCode: error.status,
              errorMessage: error.message,
              errorType: 'RefreshTokenExpired',
              backendError: error?.error ? JSON.stringify(error.error).substring(0, 500) : 'null',
              isTerminal: isTerminalUser(),
              currentRoute: router.url
            }
          }
        );

        // Setze isRefreshing zurück um weitere Requests nicht zu blockieren
        isRefreshing = false;
        refreshTokenSubject.next(null);

        // KRITISCH: Terminal-Rolle darf NICHT automatisch ausgeloggt werden!
        // Terminal muss immer verfügbar bleiben für Mitarbeiter-Einstempelungen
        if (isTerminalUser()) {
          // LOGGING: Terminal-Session abgelaufen - aber kein Redirect
          logger.logWarning('Auth Interceptor: Terminal session expired - staying on terminal page', LogCategory.Security, {
            componentName: 'AuthInterceptor',
            currentRoute: router.url,
            action: 'no-redirect-terminal-mode',
            message: 'Administrator muss sich neu einloggen - Terminal bleibt verfügbar'
          });

          // Navigiere zum Terminal-Dashboard wenn nicht bereits dort
          // Das Terminal-Dashboard wird eine Warning-Nachricht anzeigen
          if (!router.url.includes('/terminal-dashboard')) {
            router.navigateByUrl('/terminal-dashboard');
          }

          // Setze Flag in localStorage für Terminal-Dashboard
          localStorage.setItem('terminalSessionExpired', 'true');

          // Fehler werfen damit Component den Fehler abfangen kann
          return throwError(() => ({
            ...error,
            terminalSessionExpired: true,
            message: 'Terminal session expired - please re-login'
          }));
        }

        // Für normale User: Redirect zur Login-Seite
        clearAuthAndRedirect(router, logger);

        return throwError(() => error);
      }

      // Handle 402 Payment Required (trial expired / subscription restricted)
      if (error instanceof HttpErrorResponse && error.status === 402) {
        logger.logWarning('Auth Interceptor: 402 Payment Required - subscription restriction', LogCategory.Security, {
          componentName: 'AuthInterceptor',
          url: authReq.url,
          method: authReq.method,
          phase: error.error?.notifications?.[0]?.details?.phase || 'unknown'
        });

        // Refresh subscription data so banner/wall reacts
        try {
          const subscriptionService = inject(SubscriptionService);
          subscriptionService.refreshSubscriptionData();
        } catch { /* Service may not be available in all contexts */ }

        return throwError(() => error);
      }

      return throwError(() => error);
    })
  );
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  router: Router,
  authService: AuthService,
  logger: ClientLoggerService
): Observable<HttpEvent<any>> {
  // Check public routes
  if (isPublicRoute(router) && isOptionalEndpoint(request)) {
    logger.logInfo('Auth Interceptor: 401 on public route with optional endpoint - ignoring', LogCategory.Security, {
      componentName: 'AuthInterceptor',
      url: request.url,
      route: router.url
    });

    return throwError(() => ({
      status: 401,
      message: 'Unauthorized - but staying on public page',
    }));
  }

  // Synchronized refresh token handling mit korrekter Race Condition Behandlung
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    // Das Token, mit dem DIESER Request gescheitert ist — nicht das aktuelle aus dem Speicher.
    // Ist es inzwischen ersetzt, erkennt der AuthService den Nachzügler und erneuert nicht noch
    // einmal (Kunde 29: genau diese zweite Erneuerung legte das ersetzte Token erneut vor).
    const token = usedAccessToken(request) ?? localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    if (!token || !refreshToken) {
      isRefreshing = false;

      // Terminal-User ohne RefreshToken (z.B. alte Pairing-Version): nicht ausloggen
      if (isTerminalUser()) {
        logger.logWarning('Auth Interceptor: Terminal session has no refresh token - staying on terminal page', LogCategory.Security, {
          componentName: 'AuthInterceptor',
          url: request.url,
          action: 'no-redirect-terminal-mode'
        });

        localStorage.setItem('terminalSessionExpired', 'true');
        if (!router.url.includes('/terminal-dashboard')) {
          router.navigateByUrl('/terminal-dashboard');
        }
        return throwError(() => ({
          status: 401,
          terminalSessionExpired: true,
          message: 'Terminal session expired - please re-pair',
        }));
      }

      // LOGGING: Kein Token/Refresh-Token verfügbar
      logger.logWarning('Auth Interceptor: No token or refresh token available - redirecting to login', LogCategory.Security, {
        componentName: 'AuthInterceptor',
        url: request.url,
        method: request.method,
        hasToken: !!token,
        hasRefreshToken: !!refreshToken
      });

      clearAuthAndRedirect(router, logger);
      return throwError(() => ({
        status: 401,
        message: 'No token available',
      }));
    }

    // LOGGING: Starte Token-Refresh
    logger.logInfo('Auth Interceptor: Starting token refresh', LogCategory.Security, {
      componentName: 'AuthInterceptor',
      url: request.url,
      method: request.method,
      operation: 'token-refresh-start'
    });

    return authService.tryRefreshingTokens(token).pipe(
      switchMap((response: AuthResponseDto) => {
        isRefreshing = false;
        refreshTokenSubject.next(response.Token);

        // LOGGING: Token-Refresh erfolgreich
        logger.logInfo('Auth Interceptor: Token refresh successful - retrying original request', LogCategory.Security, {
          componentName: 'AuthInterceptor',
          url: request.url,
          method: request.method,
          operation: 'token-refresh-success'
        });

        // Retry original request mit neuem Token
        return next(addTokenHeader(request, response.Token));
      }),
      catchError((err) => {
        isRefreshing = false;
        refreshTokenSubject.next(null);

        // LOGGING: Token-Refresh fehlgeschlagen
        logger.logError(err, {
          componentName: 'AuthInterceptor',
          category: LogCategory.Security,
          properties: {
            url: request.url,
            method: request.method,
            operation: 'token-refresh-failed',
            errorStatus: err?.status,
            errorMessage: err?.message,
            errorType: err?.error?.ErrorType
          }
        });

        // Terminal-User: auf Dashboard bleiben statt ausloggen
        if (isTerminalUser()) {
          logger.logWarning('Auth Interceptor: Terminal refresh failed - staying on terminal page', LogCategory.Security, {
            componentName: 'AuthInterceptor',
            action: 'no-redirect-terminal-mode'
          });
          localStorage.setItem('terminalSessionExpired', 'true');
          if (!router.url.includes('/terminal-dashboard')) {
            router.navigateByUrl('/terminal-dashboard');
          }
          return throwError(() => err);
        }

        clearAuthAndRedirect(router, logger);
        return throwError(() => err);
      }),
      finalize(() => {
        isRefreshing = false;
      })
    );
  } else {
    // LOGGING: Warte auf laufenden Token-Refresh
    logger.logInfo('Auth Interceptor: Waiting for ongoing token refresh', LogCategory.Security, {
      componentName: 'AuthInterceptor',
      url: request.url,
      method: request.method,
      operation: 'token-refresh-waiting'
    });

    // An die laufende Erneuerung anhängen. Der AuthService teilt sie (Single-Flight) — auch mit
    // dem Timer, den diese Sperre hier nie kannte. Vorher warteten parallele Requests auf
    // `refreshTokenSubject`; scheiterte die Erneuerung, bekam das Subject `null` und die
    // wartenden Requests hingen für immer.
    return authService.tryRefreshingTokens(usedAccessToken(request) ?? localStorage.getItem('token')).pipe(
      switchMap((response: AuthResponseDto) => {
        logger.logInfo('Auth Interceptor: Token refresh completed - retrying request', LogCategory.Security, {
          componentName: 'AuthInterceptor',
          url: request.url,
          method: request.method,
          operation: 'token-refresh-retry'
        });

        return next(addTokenHeader(request, response.Token));
      }),
      catchError((err) => throwError(() => err))
    );
  }
}

/** Das Bearer-Token, mit dem der Request tatsächlich abgeschickt wurde. */
function usedAccessToken(request: HttpRequest<any>): string | null {
  const header = request.headers.get('Authorization');
  return header?.startsWith('Bearer ') ? header.substring('Bearer '.length) : null;
}

function clearAuthAndRedirect(router: Router, logger: ClientLoggerService): void {
  // Behalte Remember Me Informationen
  const rememberMe = localStorage.getItem('rememberMe');
  const rememberedEmail = localStorage.getItem('rememberedEmail');
  const rememberedTerminal = localStorage.getItem('rememberedTerminal');

  // LOGGING: Logout und Redirect
  logger.logInfo('Auth Interceptor: Clearing auth tokens and redirecting to login', LogCategory.Security, {
    componentName: 'AuthInterceptor',
    currentRoute: router.url,
    hasRememberMe: rememberMe === 'true',
    operation: 'clear-auth-redirect'
  });

  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('terminalSessionExpired');

  // Restore Remember Me wenn aktiviert
  if (rememberMe === 'true') {
    localStorage.setItem('rememberMe', rememberMe);
    if (rememberedEmail) {
      localStorage.setItem('rememberedEmail', rememberedEmail);
    }
    if (rememberedTerminal) {
      localStorage.setItem('rememberedTerminal', rememberedTerminal);
    }
  }

  if (!isPublicRoute(router)) {
    router.navigateByUrl('/login');
  }
}

function addTokenHeader(
  request: HttpRequest<any>,
  token: string
): HttpRequest<any> {
  return request.clone({
    headers: request.headers.set('Authorization', 'Bearer ' + token),
  });
}

function isRefreshRequest(request: HttpRequest<any>): boolean {
  return request.url.includes('/RefreshToken');
}

/** Routen, die nur ein Terminal-Token aufrufen kann. */
function isTerminalRoute(request: HttpRequest<any>): boolean {
  return request.url.includes('/Terminal/');
}

/**
 * Die PIN-Prüfung am Terminal darf NICHT über den 401-Auto-Refresh laufen.
 *
 * Das Backend beantwortet eine falsche PIN mit `401 INVALID_PIN` — fachlich, nicht
 * sicherheitstechnisch. Der Interceptor hielt das für ein abgelaufenes Token, holte ein neues und
 * **wiederholte die Anfrage**. Damit zählte jeder Tippfehler zweimal auf den Fehlversuchszähler,
 * und die Sperre (5 Versuche, 30 Minuten) griff schon nach etwa drei Eingaben. Der Nutzer sah
 * dazu nur „PIN falsch" und konnte sich nicht erklären, warum das Konto so schnell zu war.
 */
function isPinValidationRequest(request: HttpRequest<any>): boolean {
  return request.url.includes('/Terminal/ValidatePinAndClockEvent');
}

function isAuthEndpoint(request: HttpRequest<any>): boolean {
  return (
    request.url.includes('/ApplicationUser/Login') ||
    request.url.includes('/TerminalUser/LoginTerminal') ||
    request.url.includes('/ApplicationUser/Logout') ||
    request.url.includes('/ApplicationUser/InvalidateAllTokens')
  );
}

function isPublicRoute(router: Router): boolean {
  const publicRoutes = [
    '/login',
    '/demo',
    '/registration',
    '/forgotpassword',
    '/reset-password',
    '/email-confirmation',
    '/change-password',
    '/accept-invitation',
    // Die Kopplungsseite eines neuen Terminals ist bewusst ohne Anmeldung erreichbar
    // (app-routing.module.ts trägt dort keinen AuthGuard). Sie fehlte hier trotzdem, und das
    // machte sie faktisch unerreichbar: Beim Betreten lädt die App die Firmeneinstellungen,
    // bekommt ohne Token 401, und clearAuthAndRedirect() warf zur Anmeldung. Wer den QR-Code
    // scannte, landete auf /login — die einzige Kopplung, die blieb, war die manuelle Eingabe.
    '/terminal-pairing',
  ];
  return publicRoutes.some((route) => router.url.startsWith(route));
}

function isOptionalEndpoint(request: HttpRequest<any>): boolean {
  const optionalEndpoints = [
    '/GetCompanySettings',
    '/GetUserSettings',
    '/UpdateUserSettings',
  ];
  return optionalEndpoints.some((endpoint) => request.url.includes(endpoint));
}

function isStaticAssetRequest(request: HttpRequest<any>): boolean {
  const url = request.url.toLowerCase();
  // Skip translation files, fonts, images, and other static assets
  return (
    url.includes('/assets/i18n/') ||
    url.endsWith('.json') && (url.includes('/i18n/') || url.includes('/assets/')) ||
    url.endsWith('.woff') ||
    url.endsWith('.woff2') ||
    url.endsWith('.ttf') ||
    url.endsWith('.eot') ||
    url.endsWith('.svg') ||
    url.endsWith('.png') ||
    url.endsWith('.jpg') ||
    url.endsWith('.jpeg') ||
    url.endsWith('.gif') ||
    url.endsWith('.webp') ||
    url.endsWith('.css') ||
    url.endsWith('.js') && !url.includes('/api/')
  );
}

function isTerminalUser(): boolean {
  const token = localStorage.getItem('token');
  if (!token) return false;

  try {
    const payload = JSON.parse(window.atob(token.split('.')[1]));
    // JWT contains short form 'role' (mapped by JwtSecurityTokenHandler)
    // Fallback to long form for compatibility
    const roles = payload.role
      || payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

    if (Array.isArray(roles)) {
      return roles.some((r: string) => r.toUpperCase() === 'TERMINAL');
    }
    return (roles || '').toString().toUpperCase() === 'TERMINAL';
  } catch {
    return false;
  }
}